<template>
  <div
    class="read-more-less"
    :class="{ 'read-more-less--mobile-only': mobileOnly }"
  >
    <input
      v-if="enableTruncation && content"
      :id="`${liquid.sectionId}-read-more`"
      v-model="isReadMoreChecked"
      class="read-more-less__read-more-input"
      type="checkbox"
    />

    <div
      class="read-more-less__content"
      :style="type === 'lines' ? `--lines: ${lines}` : ''"
    >
      <div
        class="read-more-less__truncated-content rte"
        :class="`read-more-less__truncated-content--${type}`"
        :aria-hidden="type === 'characters'"
      >
        <rte-content v-if="rteContent" :content="truncatedContent" />

        <span v-else v-html="truncatedContent" />
      </div>

      <div
        v-if="type === 'characters'"
        class="read-more-less__characters-full-content rte"
      >
        <rte-content v-if="rteContent" :content="content" />

        <span v-else v-html="content" />
      </div>
    </div>

    <label
      v-if="enableTruncation && content"
      class="read-more-less__read-more-label"
      :class="labelClasses"
      :for="`${liquid.sectionId}-read-more`"
      v-text="
        isReadMoreChecked
          ? $string('general.read_less')?.toLowerCase()
          : $string('general.read_more')?.toLowerCase()
      "
    />
  </div>
</template>

<script>
/**
 * Vue: Read more/less (read-more-less)
 * -----------------------------------------------------------------------------
 * Snippet to add functionality of Read more/less for long texts.
 *
 * @param {Number} [characters]  - Number of characters to truncate to.
 * @param {String} content - The text content to truncate.
 * @param {Boolean} [enableTruncation] - Enable truncation.
 * @param {String} [labelClasses] - Additional label classes to render.
 * @param {Object} section - Liquid section ID.
 * @param {Boolean} [mobileOnly] - If set to true, the read more/less
 * button will only show on mobile.
 * @param {Number} [lines]  - Number of lines to truncate to.
 * @param {Boolean} [rteContent] - Render content using RTE content.
 *
 */
import RteContent from '~async/rte-content/rte-content.vue';

export default {
  name: 'ReadMoreLess',

  components: {
    RteContent,
  },

  props: {
    characters: {
      type: Number,
      default: 0,
    },
    content: {
      type: String,
      default: '',
    },
    enableTruncation: {
      type: Boolean,
      default: true,
    },
    labelClasses: {
      type: String,
      default:
        'text-mobile-paragraph-m-regular-link text-desktop-paragraph-m-regular-link',
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    mobileOnly: {
      type: Boolean,
      default: true,
    },
    lines: {
      type: Number,
      default: 5,
    },
    rteContent: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isReadMoreChecked: false,
    };
  },

  computed: {
    /**
     * Compute truncated content.
     * @returns {String}
     */
    truncatedContent() {
      if (this.type === 'characters') {
        const count = this.characters - 3;
        return `${this.content.slice(0, count)}... `;
      }

      return this.content;
    },

    /**
     * Compute type of truncation.
     * @returns {String}
     */
    type() {
      return this.characters ? 'characters' : 'lines';
    },
  },
};
</script>

<style lang="scss">
@import './read-more-less';
</style>
